import type { QueryDSL, UnsupportedResult } from '../types/QueryTypes';
import { ALL_FIELDS } from '../metadata/fieldMetadata';
import { buildPlannerPrompt } from '../prompts/PlannerPrompt';
import { callPlanner, parseQueryDSL } from '../llm/BedrockClient';
import { validateQuery } from '../query/QueryValidator';
function normalizeFilterValues(query: QueryDSL): void {
  if (query.filters) {
    for (const filter of query.filters) {
      filter.field = filter.field.toLowerCase();
      const meta = ALL_FIELDS[filter.field];
      if (!meta) continue;
      if (typeof filter.value === 'string' && filter.value.length > 0) {
        if (meta.type === 'number') {
          let v = filter.value.trim();
          v = v.replace(/[$€£]/g, '');
          v = v.replace(/\bGNARR\b/gi, '');
          v = v.replace(/\bARR\b/gi, '');
          v = v.replace(/\brevenue\b/gi, '');
          v = v.replace(/\bamount\b/gi, '');
          v = v.replace(/\bvalue\b/gi, '');
          v = v.trim();
          const kMatch = v.match(/^([\d,.]+)\s*[Kk]$/);
          if (kMatch) {
            v = kMatch[1].replace(/,/g, '');
            filter.value = parseFloat(v) * 1_000;
          } else {
            const mMatch = v.match(/^([\d,.]+)\s*[Mm]$/);
            if (mMatch) {
              v = mMatch[1].replace(/,/g, '');
              filter.value = parseFloat(v) * 1_000_000;
            } else {
              v = v.replace(/,/g, '');
              const num = Number(v);
              if (!isNaN(num)) filter.value = num;
            }
          }
        } else if (meta.type === 'string' && ['region', 'sub_region'].includes(filter.field)) {
          let v = filter.value.trim();
          const lower = v.toLowerCase();
          if (lower.startsWith(filter.field.replace('_', ' ') + ' ')) {
            v = v.slice(filter.field.replace('_', ' ').length).trim();
          } else if (lower.startsWith(filter.field + ' ')) {
            v = v.slice(filter.field.length).trim();
          } else if (lower.startsWith('region ')) {
            v = v.slice(7).trim();
          }
          if (v.length > 0) filter.value = v;
        }
      }
    }
  }
  if (query.sort) {
    for (const sort of query.sort) {
      sort.field = sort.field.toLowerCase();
    }
  }
  if (query.groupBy) {
    query.groupBy = query.groupBy.toLowerCase();
  }
  if (query.aggregate) {
    for (const agg of query.aggregate) {
      agg.field = agg.field.toLowerCase();
    }
  }
}
export interface PlannerResult {
  type: 'query' | 'unsupported' | 'error';
  query?: QueryDSL;
  unsupported?: UnsupportedResult;
  error?: string;
}
export async function planQuery(userQuestion: string, signal?: AbortSignal): Promise<PlannerResult> {
  try {
    const prompt = buildPlannerPrompt(userQuestion);
    // console.log('[NLQ] Prompt sent to LLM:\n', prompt);
    const rawResponse = await callPlanner(prompt, signal);
    console.log('[NLQ] Raw LLM response:', rawResponse);
    const parsed = parseQueryDSL(rawResponse);
    if (!parsed) {
      console.error('[NLQ] Failed to parse LLM response:', rawResponse);
      return {
        type: 'error',
        error: 'Failed to parse LLM response as JSON'
      };
    }
    if (parsed.unsupported) {
      return {
        type: 'unsupported',
        unsupported: {
          unsupported: true,
          suggestion: parsed.suggestion as string | undefined
        }
      };
    }
    const query = parsed as unknown as QueryDSL;
    if (!query.dataset) {
      return {
        type: 'error',
        error: 'Query missing dataset field'
      };
    }
    normalizeFilterValues(query);
    console.log('[NLQ] Parsed query after normalization:', JSON.stringify(query, null, 2));
    const validation = validateQuery(query);
    console.log('[NLQ] Validation result:', validation);
    if (!validation.valid) {
      return {
        type: 'unsupported',
        unsupported: {
          unsupported: true,
          suggestion: validation.suggestion ?? validation.error
        }
      };
    }
    return {
      type: 'query',
      query
    };
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Unknown error';
    return {
      type: 'error',
      error: msg
    };
  }
}